import React, { useState, useEffect } from 'react';

const AdminGuests = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roomFilter, setRoomFilter] = useState('all');
  const [allGuests, setAllGuests] = useState([]);
  const [stats, setStats] = useState({
    totalGuests: 0,
    occupiedRooms: 0,
    maleGuests: 0,
    femaleGuests: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch guests and stats on component mount
  useEffect(() => {
    fetchGuests();
    fetchStats();
  }, []);

  const fetchGuests = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/guests');
      const data = await response.json();
      
      if (data.success) {
        setAllGuests(data.guests);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching guests:', error);
      alert('Error loading guests');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/guests/stats');
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchGuestDetails = async (guestId) => {
    try {
      const response = await fetch(`https://hs.learninghub.ind.in/api/admin/guest/${guestId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedGuest(data.guest);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching guest details:', error);
      alert('Error loading guest details');
    }
  };

  // Get unique rooms for filter
  const uniqueRooms = [...new Set(allGuests.map(g => g.room_number))].sort();

  // Filter guests
  const filteredGuests = allGuests.filter(guest => {
    const matchesSearch = guest.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         guest.room_number.includes(searchQuery) ||
                         guest.phone.includes(searchQuery);
    const matchesRoom = roomFilter === 'all' || guest.room_number === roomFilter;
    return matchesSearch && matchesRoom;
  });

  // Group by room
  const guestsByRoom = filteredGuests.reduce((acc, guest) => {
    if (!acc[guest.room_number]) {
      acc[guest.room_number] = [];
    }
    acc[guest.room_number].push(guest);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const styles = {
    container: {
      padding: '2rem',
      background: '#f5f7fa',
      minHeight: '100vh',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#333',
      margin: '0 0 0.5rem 0',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#666',
      margin: 0,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    statCard: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      textAlign: 'center',
      transition: 'transform 0.2s',
    },
    statValue: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#2c5aa0',
      margin: '0 0 0.5rem 0',
    },
    statLabel: {
      fontSize: '0.95rem',
      color: '#666',
      fontWeight: 600,
    },
    filterSection: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      marginBottom: '2rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
    },
    input: {
      flex: 1,
      minWidth: '250px',
      padding: '0.8rem 1rem',
      fontSize: '1rem',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      outline: 'none',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    select: {
      padding: '0.8rem 1rem',
      fontSize: '1rem',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      outline: 'none',
      cursor: 'pointer',
      background: 'white',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    roomSection: {
      marginBottom: '2rem',
    },
    roomHeader: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#2c5aa0',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    guestCard: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      marginBottom: '1rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      border: '2px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    avatar: {
      width: '70px',
      height: '70px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '2rem',
      fontWeight: 700,
      flexShrink: 0,
    },
    guestInfo: {
      flex: 1,
      marginLeft: '1.5rem',
    },
    guestName: {
      fontSize: '1.3rem',
      fontWeight: 700,
      color: '#333',
      margin: '0 0 0.5rem 0',
    },
    guestDetails: {
      fontSize: '0.95rem',
      color: '#666',
      margin: '0.3rem 0',
    },
    primaryBadge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 600,
      marginLeft: '0.5rem',
    },
    idSection: {
      textAlign: 'right',
    },
    idType: {
      fontSize: '0.85rem',
      color: '#666',
      fontWeight: 600,
      margin: '0 0 0.3rem 0',
    },
    idNumber: {
      fontSize: '1.1rem',
      color: '#2c5aa0',
      fontWeight: 700,
      margin: 0,
    },
    loading: {
      textAlign: 'center',
      padding: '3rem',
      fontSize: '1.2rem',
      color: '#666',
    },
    emptyState: {
      background: 'white',
      padding: '3rem',
      borderRadius: '12px',
      textAlign: 'center',
      color: '#666',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem',
      backdropFilter: 'blur(5px)',
    },
    modalContent: {
      background: 'white',
      borderRadius: '20px',
      maxWidth: '800px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    modalHeader: {
      padding: '2rem',
      borderBottom: '2px solid #f0f0f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderRadius: '20px 20px 0 0',
    },
    modalTitle: {
      fontSize: '1.8rem',
      fontWeight: 700,
      margin: 0,
    },
    closeBtn: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      cursor: 'pointer',
      fontSize: '1.5rem',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
    },
    modalBody: {
      padding: '2rem',
    },
    infoSection: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.3rem',
      fontWeight: 700,
      color: '#333',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
    },
    infoItem: {
      padding: '1rem',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
    },
    infoLabel: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: 600,
    },
    infoValue: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#1f2937',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          Loading guests...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>👥 All Guests</h1>
        <p style={styles.subtitle}>View and manage all current guests</p>
      </div>

      {/* Statistics */}
      <div style={styles.statsGrid}>
        <div 
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statValue}>{stats.totalGuests}</div>
          <div style={styles.statLabel}>Total Guests</div>
        </div>
        <div 
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statValue}>{stats.occupiedRooms}</div>
          <div style={styles.statLabel}>Occupied Rooms</div>
        </div>
        <div 
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statValue}>{stats.maleGuests}</div>
          <div style={styles.statLabel}>Male Guests</div>
        </div>
        <div 
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statValue}>{stats.femaleGuests}</div>
          <div style={styles.statLabel}>Female Guests</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterSection}>
        <input
          type="text"
          placeholder="🔍 Search by name, room, or phone..."
          style={styles.input}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={(e) => e.target.style.borderColor = '#2c5aa0'}
          onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
        />
        <select
          style={styles.select}
          value={roomFilter}
          onChange={(e) => setRoomFilter(e.target.value)}
        >
          <option value="all">All Rooms</option>
          {uniqueRooms.map(room => (
            <option key={room} value={room}>Room {room}</option>
          ))}
        </select>
      </div>

      {/* Guests List Grouped by Room */}
      {Object.keys(guestsByRoom).sort((a, b) => {
        // Sort rooms numerically
        return parseInt(a) - parseInt(b);
      }).map(room => (
        <div key={room} style={styles.roomSection}>
          <h3 style={styles.roomHeader}>
            🚪 Room {room} - {guestsByRoom[room].length} Guest(s)
          </h3>
          {guestsByRoom[room].map(guest => (
            <div
              key={guest.id}
              style={styles.guestCard}
              onClick={() => fetchGuestDetails(guest.id)}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#2c5aa0';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(44, 90, 160, 0.2)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={styles.avatar}>
                {guest.photo_url ? (
                  <img 
                    src={guest.photo_url} 
                    alt={guest.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  guest.name.charAt(0)
                )}
              </div>
              <div style={styles.guestInfo}>
                <h4 style={styles.guestName}>
                  {guest.name}
                  {guest.is_primary === 1 && (
                    <span style={styles.primaryBadge}>Primary Guest</span>
                  )}
                </h4>
                <p style={styles.guestDetails}>
                  {guest.age} years • {guest.gender} • {guest.booking_type || 'Standard'}
                </p>
                <p style={styles.guestDetails}>
                  📱 {guest.phone}
                </p>
                <p style={styles.guestDetails}>
                  📧 {guest.email || '-'}
                </p>
                <p style={styles.guestDetails}>
                  📅 Check-in: {formatDate(guest.check_in_date)} • Check-out: {formatDate(guest.check_out_date)} ({guest.number_of_nights} nights)
                </p>
              </div>
              <div style={styles.idSection}>
                <p style={styles.idType}>🆔 {guest.id_type}</p>
                <p style={styles.idNumber}>{guest.id_number}</p>
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                  {guest.room_type} Room
                </p>
              </div>
            </div>
          ))}
        </div>
      ))}

      {filteredGuests.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <p style={{ fontSize: '1.5rem', margin: 0 }}>No guests found</p>
          <p style={{ fontSize: '1rem', marginTop: '0.5rem' }}>Try adjusting your search or filter</p>
        </div>
      )}

      {/* Guest Details Modal */}
      {showModal && selectedGuest && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                👤 {selectedGuest.name}
              </h2>
              <button
                style={styles.closeBtn}
                onClick={() => setShowModal(false)}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Personal Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>
                  <span>👤</span>
                  Personal Information
                </h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Full Name</div>
                    <div style={styles.infoValue}>{selectedGuest.name}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Age</div>
                    <div style={styles.infoValue}>{selectedGuest.age} years</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Gender</div>
                    <div style={styles.infoValue}>{selectedGuest.gender}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Phone</div>
                    <div style={styles.infoValue}>{selectedGuest.phone}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Email</div>
                    <div style={styles.infoValue}>{selectedGuest.email || '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Guest Type</div>
                    <div style={styles.infoValue}>
                      {selectedGuest.is_primary ? 'Primary Guest' : 'Additional Guest'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Address */}
              {selectedGuest.address && (
                <div style={styles.infoSection}>
                  <h3 style={styles.sectionTitle}>
                    <span>📍</span>
                    Address
                  </h3>
                  <div style={styles.infoItem}>
                    <div style={styles.infoValue}>{selectedGuest.address}</div>
                  </div>
                </div>
              )}

              {/* ID Verification */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>
                  <span>🆔</span>
                  ID Verification
                </h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>ID Type</div>
                    <div style={styles.infoValue}>{selectedGuest.id_type}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>ID Number</div>
                    <div style={styles.infoValue}>{selectedGuest.id_number}</div>
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>
                  <span>📅</span>
                  Booking Details
                </h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Booking ID</div>
                    <div style={styles.infoValue}>{selectedGuest.booking_code}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Room Number</div>
                    <div style={styles.infoValue}>Room {selectedGuest.room_number}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Room Type</div>
                    <div style={styles.infoValue}>{selectedGuest.room_type}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Booking Type</div>
                    <div style={styles.infoValue}>{selectedGuest.booking_type || '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Check-in Date</div>
                    <div style={styles.infoValue}>{formatDate(selectedGuest.check_in_date)}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Check-out Date</div>
                    <div style={styles.infoValue}>{formatDate(selectedGuest.check_out_date)}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Number of Nights</div>
                    <div style={styles.infoValue}>{selectedGuest.number_of_nights} nights</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Total Guests</div>
                    <div style={styles.infoValue}>{selectedGuest.number_of_guests} guest(s)</div>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>
                  <span>💰</span>
                  Payment Details
                </h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Total Amount</div>
                    <div style={styles.infoValue}>₹{parseFloat(selectedGuest.total_amount).toLocaleString('en-IN')}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Advance Paid</div>
                    <div style={{ ...styles.infoValue, color: '#4CAF50' }}>
                      ₹{parseFloat(selectedGuest.advance_paid).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Balance Due</div>
                    <div style={{ ...styles.infoValue, color: '#f44336' }}>
                      ₹{parseFloat(selectedGuest.balance_due).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Booking Status</div>
                    <div style={styles.infoValue}>{selectedGuest.booking_status}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGuests;